import React, { useState, useCallback, useEffect } from "react";
import Card from "components/card";
import PureDropDown, { DropdownOption } from "components/pure-dropdown";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

interface MonthFilterProps {
  selectedMonth: string; // Format: "YYYY-MM"
  onMonthChange: (month: string) => void;
  isLoading?: boolean;
  title?: string;
  description?: string;
}

const MonthFilter: React.FC<MonthFilterProps> = ({
  selectedMonth,
  onMonthChange,
  isLoading = false,
  title,
  description
}) => {
  const { t } = useTranslation();
  const defaultTitle = title || t("usageOverview.monthFilterTitle");
  const defaultDescription = description || t("usageOverview.monthFilterDescription");
  // Generate list of 12 months from current month back
  const generateMonthOptions = (): DropdownOption[] => {
    const months: DropdownOption[] = [];
    const currentDate = dayjs();
    
    for (let i = 0; i < 12; i++) {
      const monthDate = currentDate.subtract(i, 'month');
      const value = monthDate.format('YYYY-MM');
      const label = monthDate.format('MMMM YYYY');
      months.push({ value, label });
    }
    
    return months;
  };

  const monthOptions = generateMonthOptions();

  const handleMonthChange = useCallback((value: string | string[]) => {
    const monthValue = Array.isArray(value) ? value[0] : value;
    if (monthValue) {
      onMonthChange(monthValue);
    }
  }, [onMonthChange]);

  return (
    <Card extra="p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-navy-700 dark:text-white">
          {defaultTitle}
        </h3>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          {defaultDescription}
        </p>
        {selectedMonth && (
          <div className="mt-1 text-xs text-blue-600 dark:text-blue-400">
            📅 {t("usageOverview.currentlyShowing")}: {dayjs(selectedMonth).format('MMMM YYYY')}
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <PureDropDown
            options={monthOptions}
            value={selectedMonth}
            onChange={handleMonthChange}
            placeholder={t("usageOverview.selectMonth")}
            label={t("usageOverview.selectMonth")}
            disabled={isLoading}
            showClearButton={false}
            className="text-xs"
          />
        </div>
        {isLoading && (
          <div className="flex items-center text-xs text-gray-500">
            ⏳ {t("common.loading")}
          </div>
        )}
      </div>
    </Card>
  );
};

export default MonthFilter;
